import { useEffect, useMemo, useRef } from 'react'
import { CheckCircle2, FileText, UploadCloud, X } from 'lucide-react'
import { Button, Chip, Drawer } from '@/components/ui'
import {
  useSalesIntakeUploadQueue,
  type UploadQueueItem,
} from '@/shell/services/sales-intake-uploader'
import { useQueryClient } from '@tanstack/react-query'

const ACCEPT_ATTR = '.pdf,.PDF,.xlsx,.XLSX,.eml,.EML,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

export interface SalesIntakeUploadDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called once per drawer close-cycle so the host can invalidate
   *  list / stats queries. */
  onClose?: () => void
}

/**
 * Drawer-based batch uploader for customer sales orders.
 *
 * Flow:
 *   1. User drops N PDFs / XLSX onto the dropzone (or clicks the
 *      hidden file input). Files queue.
 *   2. `useSalesIntakeUploadQueue` processes up to 3 concurrently;
 *      each upload returns an intake_id and the server enqueues
 *      Celery extraction.
 *   3. The host page's `useIntakeList` adaptive-poll picks up
 *      pending_extraction → extraction_running → extraction_complete
 *      transitions automatically.
 *   4. Per-row final status (review / confirmed / rejected) is owned
 *      by the detail drawer (Chunk 5).
 *
 * Non-modal: user can click back to the master list while uploads run.
 * Mirrors CofaBatchUploader but simpler — no inline plan-confirm
 * branching; review happens in the dedicated detail drawer.
 */
export function SalesIntakeUploadDrawer({
  open,
  onOpenChange,
  onClose,
}: SalesIntakeUploadDrawerProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const qc = useQueryClient()

  const { items, enqueue, removeItem, clearTerminal, busy } =
    useSalesIntakeUploadQueue({
      onItemUploaded: () => {
        qc.invalidateQueries({ queryKey: ['sales-inbox'] })
      },
    })

  useEffect(() => {
    if (!open) onClose?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const summary = useMemo(() => {
    const counts = { queued: 0, uploading: 0, uploaded: 0, error: 0 }
    for (const it of items) counts[it.status] += 1
    return counts
  }, [items])

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files
    if (files && files.length > 0) enqueue(files)
    e.target.value = ''
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    const files = e.dataTransfer?.files
    if (files && files.length > 0) enqueue(files)
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      size="wide"
      title="Upload Sales Orders"
      meta="PDF, XLSX, or .eml customer orders. CARLA reviews each one in chat, populates the order, and surfaces what needs your decision. You confirm to Odoo."
      headerActions={
        <div className="flex items-center gap-2">
          {summary.queued + summary.uploading > 0 ? (
            <Chip size="xs" variant="info">
              {summary.queued + summary.uploading} in flight
            </Chip>
          ) : null}
          {summary.uploaded > 0 ? (
            <Chip size="xs" variant="success">
              {summary.uploaded} uploaded
            </Chip>
          ) : null}
          {summary.error > 0 ? (
            <Chip size="xs" variant="danger">
              {summary.error} error
            </Chip>
          ) : null}
        </div>
      }
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={clearTerminal}
            disabled={summary.uploaded + summary.error === 0}
          >
            Clear finished
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            Done
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          className="flex flex-col items-center justify-center gap-2 rounded-sm border-2 border-dashed border-border-default bg-surface-sunk px-6 py-10 text-center"
        >
          <UploadCloud size={28} className="text-tertiary" />
          <p className="m-0 text-sm text-secondary">
            Drag &amp; drop sales-order files here
          </p>
          <p className="m-0 text-xs text-tertiary">or</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT_ATTR}
            className="hidden"
            onChange={onPick}
          />
          <Button
            variant="primary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            Choose files
          </Button>
          <p className="m-0 text-xs text-tertiary">
            PDF, XLSX, EML — up to 25 MB per file
          </p>
        </div>

        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
            <FileText size={20} className="text-tertiary opacity-50" />
            <p className="m-0 text-sm text-tertiary">No files queued yet.</p>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {items.map((it) => (
              <UploadRow
                key={it.clientId}
                item={it}
                onRemove={() => removeItem(it.clientId)}
              />
            ))}
          </ul>
        )}
      </div>
    </Drawer>
  )
}

function UploadRow({
  item,
  onRemove,
}: {
  item: UploadQueueItem
  onRemove: () => void
}) {
  const variant: 'info' | 'success' | 'danger' | 'neutral' =
    item.status === 'uploaded'
      ? 'success'
      : item.status === 'error'
        ? 'danger'
        : item.status === 'uploading'
          ? 'info'
          : 'neutral'
  const label =
    item.status === 'uploaded'
      ? item.deduped
        ? 'Uploaded (deduped)'
        : 'Uploaded'
      : item.status === 'error'
        ? 'Error'
        : item.status === 'uploading'
          ? 'Uploading…'
          : 'Queued'

  return (
    <li className="flex items-center gap-3 rounded-xs border border-border-subtle bg-surface px-3 py-2 text-sm">
      <FileText size={16} className="text-tertiary" />
      <span className="flex-1 truncate text-secondary">{item.file.name}</span>
      <Chip size="xs" variant={variant} dot>
        {label}
      </Chip>
      {item.status === 'uploaded' ? (
        <CheckCircle2 size={14} className="text-success" />
      ) : null}
      {item.status === 'error' && item.errorMessage ? (
        <span
          title={item.errorMessage}
          className="max-w-[16ch] truncate text-xs text-danger"
        >
          {item.errorMessage}
        </span>
      ) : null}
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${item.file.name}`}
        className="inline-flex h-5 w-5 cursor-pointer items-center justify-center rounded-xs border-none bg-transparent text-tertiary hover:bg-surface-hover hover:text-primary"
        disabled={item.status === 'uploading'}
      >
        <X size={12} />
      </button>
    </li>
  )
}
